import { useState, useEffect } from 'react'
import { Play, RefreshCw, Clock, Trash2, Copy, CheckCircle2, AlertCircle, BarChart3, Info, Key, User, Layers } from 'lucide-react'
import type { AIService } from '@/lib/supabase-client'
import { buildServiceRequest, formatHeaders } from '@/lib/service-request-builder'
import { SERVICE_TYPES } from '@/constants/service-types'
import { SERVICE_CATEGORIES } from '@/constants/service-categories'

// API 调用辅助函数
const apiCall = async (endpoint: string, options: RequestInit = {}) => {
  const token = localStorage.getItem('admin_token')
  if (!token) {
    throw new Error('未登录')
  }

  const response = await fetch(endpoint, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || '请求失败')
  }

  return response.json()
}

// 参数模板
const PARAMETER_TEMPLATES: Record<string, any> = {
  'coze-workflow': {
    parameters: {
      input: '你好，这是测试消息'
    }
  },
  'coze-agent': {
    parameters: {
      input: '你好，请介绍一下自己'
    }
  },
  'dify-workflow': {
    inputs: {
      query: '测试查询'
    },
    response_mode: 'blocking'
  },
  'n8n-workflow': {
    data: {
      message: '测试消息'
    }
  }
}

interface TestHistory {
  id: string
  timestamp: number
  serviceId: string
  serviceName: string
  params: string
  responseTime: number
  success: boolean
  statusCode?: number
}

export default function ApiDebugPage() {
  // 服务相关
  const [services, setServices] = useState<AIService[]>([])
  const [selectedService, setSelectedService] = useState<AIService | null>(null)
  const [loadingServices, setLoadingServices] = useState(true)

  // 用户相关
  const [testUsers, setTestUsers] = useState<Array<{ id: string; email: string; name: string; balance: number }>>([])
  const [selectedTestUser, setSelectedTestUser] = useState<{ id: string; email: string; name: string; balance: number } | null>(null)
  const [testApiKeys, setTestApiKeys] = useState<Array<{ id: string; key_value?: string; key_prefix?: string; name: string }>>([])
  const [selectedApiKeyId, setSelectedApiKeyId] = useState<string>('')
  const [selectedTestApiKey, setSelectedTestApiKey] = useState<string>('')
  const [loadingTestUser, setLoadingTestUser] = useState(false)

  // 测试相关
  const [testParams, setTestParams] = useState(JSON.stringify({
    parameters: { input: '你好，这是测试' }
  }, null, 2))
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<string>('')
  const [responseTime, setResponseTime] = useState<number>(0)
  const [testSuccess, setTestSuccess] = useState<boolean | null>(null)

  // 历史记录
  const [history, setHistory] = useState<TestHistory[]>([])
  const [showHistory, setShowHistory] = useState(false)

  // 分组筛选
  const [typeFilter, setTypeFilter] = useState('all')

  // 初始化加载
  useEffect(() => {
    void fetchServices()
    void fetchTestUsers()
    loadHistory()
    loadSavedState()
  }, [])

  // 快捷键支持
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault()
        void handleTest()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [selectedService, selectedTestApiKey, testParams])

  const fetchServices = async () => {
    try {
      const result = await apiCall('/api/admin/services')
      setServices(result.data ?? [])
    } catch (error) {
      alert(`获取服务列表失败: ${error instanceof Error ? error.message : '未知错误'}`)
    } finally {
      setLoadingServices(false)
    }
  }

  const fetchTestUsers = async () => {
    setLoadingTestUser(true)
    try {
      const usersResult = await apiCall('/api/admin/users?order_by=balance&order_asc=false&limit=10')
      const users = usersResult.data ?? []

      if (users.length > 0) {
        setTestUsers(users)
        const firstUser = users[0]
        setSelectedTestUser(firstUser)
        await fetchApiKeys(firstUser.id)
      }
    } catch (error) {
      console.error('Failed to fetch test users', error)
    } finally {
      setLoadingTestUser(false)
    }
  }

  const fetchApiKeys = async (userId: string) => {
    try {
      const keysResult = await apiCall(`/api/admin/api-keys?user_id=${userId}&status=active&limit=10`)
      const keys = keysResult.data ?? []

      setTestApiKeys(keys)
      if (keys.length > 0) {
        setSelectedApiKeyId(keys[0].id)
      } else {
        setSelectedApiKeyId('')
      }
      setSelectedTestApiKey('sk_test_debug_key_for_development_only')
    } catch (error) {
      console.error('Failed to fetch API keys', error)
      setTestApiKeys([])
      setSelectedApiKeyId('')
      setSelectedTestApiKey('')
    }
  }

  const handleServiceChange = (serviceId: string) => {
    const service = services.find(s => s.id === serviceId)
    if (service) {
      setSelectedService(service)
      // 自动加载参数模板
      const template = PARAMETER_TEMPLATES[service.provider]
      if (template) {
        setTestParams(JSON.stringify(template, null, 2))
      }
      saveState({ serviceId })
    }
  }

  const handleTestUserChange = async (userId: string) => {
    const user = testUsers.find(u => u.id === userId)
    if (user) {
      setSelectedTestUser(user)
      await fetchApiKeys(user.id)
      saveState({ userId })
    }
  }

  const handleTest = async () => {
    if (!selectedService || !selectedTestApiKey) {
      alert('请选择服务和 API Key')
      return
    }

    setTesting(true)
    setTestResult('')
    setTestSuccess(null)
    setResponseTime(0)

    const workerUrl = import.meta.env.VITE_WORKER_URL || 'https://proxy.apimakt.com'
    const apiUrl = `${workerUrl}/api/v1/proxy/${selectedService.id}`

    const requestHeaders = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${selectedTestApiKey}`,
    }

    try {
      const parsedBody = JSON.parse(testParams)
      const startTime = Date.now()

      // 构建请求体
      let requestBody: any
      switch (selectedService.provider) {
        case 'coze-workflow':
          requestBody = {
            parameters: {
              parameters: parsedBody
            },
            operation_type: 'execute'
          }
          break
        case 'coze-agent':
          requestBody = {
            parameters: parsedBody,
            operation_type: 'chat'
          }
          break
        default:
          requestBody = parsedBody
          break
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: requestHeaders,
        body: JSON.stringify(requestBody),
        signal: AbortSignal.timeout((selectedService.timeout_seconds + 5) * 1000),
      })

      const endTime = Date.now()
      const totalTime = endTime - startTime
      setResponseTime(totalTime)

      const responseHeaders: Record<string, string> = {}
      response.headers.forEach((value, key) => {
        responseHeaders[key] = value
      })

      const responseText = await response.text()
      let responseData: any
      try {
        responseData = JSON.parse(responseText)
      } catch {
        responseData = responseText
      }

      const success = response.ok
      setTestSuccess(success)

      const debugInfo: Record<string, any> = {
        '测试概览': {
          成功: success,
          HTTP状态码: response.status,
          状态文本: response.statusText,
          总耗时: `${totalTime}ms`,
        },
        '请求信息': {
          接口: apiUrl,
          方法: 'POST',
          服务UUID: selectedService.id,
          服务名称: selectedService.name,
          服务类型: selectedService.provider,
          第三方服务ID: selectedService.service_id,
          测试用户: selectedTestUser ? `${selectedTestUser.email || selectedTestUser.name} (余额: ${selectedTestUser.balance})` : 'N/A',
          请求头: requestHeaders,
          请求体: requestBody,
        },
        '响应信息': {
          响应头: responseHeaders,
          响应体: responseData,
        },
      }

      setTestResult(JSON.stringify(debugInfo, null, 2))

      // 保存到历史
      saveToHistory({
        serviceId: selectedService.id,
        serviceName: selectedService.name,
        params: testParams,
        responseTime: totalTime,
        success,
        statusCode: response.status
      })
    } catch (error) {
      setTestSuccess(false)
      const errorInfo = {
        错误: true,
        错误类型: error instanceof Error ? error.constructor.name : typeof error,
        错误信息: error instanceof Error ? error.message : String(error),
        错误栈: error instanceof Error ? error.stack : undefined,
      }
      setTestResult(JSON.stringify(errorInfo, null, 2))
    } finally {
      setTesting(false)
    }
  }

  const loadTemplate = (provider: string) => {
    const template = PARAMETER_TEMPLATES[provider]
    if (template) {
      setTestParams(JSON.stringify(template, null, 2))
    }
  }

  // 历史记录功能
  const saveToHistory = (data: Omit<TestHistory, 'id' | 'timestamp'>) => {
    const newHistory: TestHistory = {
      id: Date.now().toString(),
      timestamp: Date.now(),
      ...data
    }
    const updated = [newHistory, ...history].slice(0, 50) // 保留最近 50 条
    setHistory(updated)
    localStorage.setItem('api_debug_history', JSON.stringify(updated))
  }

  const loadHistory = () => {
    try {
      const saved = localStorage.getItem('api_debug_history')
      if (saved) {
        setHistory(JSON.parse(saved))
      }
    } catch (error) {
      console.error('Failed to load history', error)
    }
  }

  const loadFromHistory = (item: TestHistory) => {
    const service = services.find(s => s.id === item.serviceId)
    if (service) {
      setSelectedService(service)
      setTestParams(item.params)
      setShowHistory(false)
    }
  }

  const clearHistory = () => {
    if (confirm('确定要清空所有历史记录吗？')) {
      setHistory([])
      localStorage.removeItem('api_debug_history')
    }
  }

  // 状态持久化
  const saveState = (partial: { serviceId?: string; userId?: string }) => {
    const current = JSON.parse(localStorage.getItem('api_debug_state') || '{}')
    localStorage.setItem('api_debug_state', JSON.stringify({ ...current, ...partial }))
  }

  const loadSavedState = () => {
    try {
      const saved = localStorage.getItem('api_debug_state')
      if (saved) {
        const state = JSON.parse(saved)
        // 等待服务和用户加载完成后再恢复状态
        setTimeout(() => {
          if (state.serviceId && services.length > 0) {
            const service = services.find(s => s.id === state.serviceId)
            if (service) {
              setSelectedService(service)
            }
          }
          if (state.userId && testUsers.length > 0) {
            const user = testUsers.find(u => u.id === state.userId)
            if (user) {
              setSelectedTestUser(user)
              void fetchApiKeys(user.id)
            }
          }
        }, 500)
      }
    } catch (error) {
      console.error('Failed to load saved state', error)
    }
  }

  // 复制功能
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
      .then(() => alert('已复制到剪贴板'))
      .catch(() => alert('复制失败'))
  }

  // 解析参数检查
  let parseError: string | null = null
  try {
    JSON.parse(testParams)
  } catch (err) {
    parseError = err instanceof Error ? err.message : '无效的 JSON'
  }

  // 构建请求预览
  const requestInfo = selectedService && selectedTestApiKey ? buildServiceRequest({
    provider: selectedService.provider,
    serviceId: selectedService.id,
    userParameters: parseError ? {} : JSON.parse(testParams)
  }, selectedTestApiKey) : null

  // 筛选服务
  const filteredServices = services.filter(service => {
    const matchesType = typeFilter === 'all' || service.provider === typeFilter
    return matchesType
  })

  // 响应时间图表数据
  const chartData = history.slice(0, 10).reverse()

  if (loadingServices) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">加载中...</p>
        </div>
      </div>
    )
  }

  return (
    <section className="min-h-screen py-6 px-4 bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">API 调试工具</h1>
            <p className="text-gray-600">快速测试和调试 API 服务</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            >
              <Clock className="w-4 h-4" />
              历史记录 ({history.length})
            </button>
            <button
              onClick={() => void fetchServices()}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            >
              <RefreshCw className="w-4 h-4" />
              刷新
            </button>
          </div>
        </div>

        {/* 选择器区域 */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* 服务选择 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Layers className="w-4 h-4 inline-block mr-1.5" />
                选择服务
              </label>
              <select
                value={selectedService?.id || ''}
                onChange={(e) => handleServiceChange(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition text-sm"
              >
                <option value="">-- 请选择服务 --</option>
                {SERVICE_CATEGORIES.map(category => {
                  const categoryServices = filteredServices.filter(s => s.category === category.id)
                  if (categoryServices.length === 0) return null
                  return (
                    <optgroup key={category.id} label={category.name}>
                      {categoryServices.map(service => (
                        <option key={service.id} value={service.id}>
                          {service.name} ({service.provider})
                        </option>
                      ))}
                    </optgroup>
                  )
                })}
              </select>
              {selectedService && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {SERVICE_TYPES.map(type => (
                    <button
                      key={type.id}
                      onClick={() => setTypeFilter(typeFilter === type.id ? 'all' : type.id)}
                      className={`px-2 py-1 text-xs rounded-md transition ${
                        typeFilter === type.id
                          ? `${type.bgColor} ${type.color}`
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {type.displayName}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* 用户选择 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <User className="w-4 h-4 inline-block mr-1.5" />
                测试用户
              </label>
              {loadingTestUser ? (
                <div className="flex items-center gap-2 text-sm text-gray-500 px-3 py-2.5">
                  <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  加载中...
                </div>
              ) : (
                <select
                  value={selectedTestUser?.id || ''}
                  onChange={(e) => void handleTestUserChange(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition text-sm"
                >
                  {testUsers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.email || user.name} - 余额: {user.balance}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* API Key 选择 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Key className="w-4 h-4 inline-block mr-1.5" />
                API Key
              </label>
              {testApiKeys.length > 0 ? (
                <select
                  value={selectedApiKeyId}
                  onChange={(e) => setSelectedApiKeyId(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition text-sm"
                >
                  {testApiKeys.map((key) => (
                    <option key={key.id} value={key.id}>
                      {key.name} {key.key_prefix ? `(${key.key_prefix}...)` : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="text-sm text-gray-500 px-3 py-2.5 bg-gray-50 rounded-lg border border-gray-200">
                  该用户暂无 API Key
                </div>
              )}
            </div>
          </div>

          {/* API Key 手动输入 */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              API Key 值（手动输入）
            </label>
            <input
              type="text"
              value={selectedTestApiKey}
              onChange={(e) => setSelectedTestApiKey(e.target.value)}
              placeholder="请粘贴完整的 API Key（以 sk_ 开头）"
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition text-sm font-mono"
            />
            <p className="mt-1 text-xs text-gray-500">
              💡 测试 Key: <code className="bg-gray-100 px-2 py-0.5 rounded">sk_test_debug_key_for_development_only</code>
            </p>
          </div>
        </div>
      </div>

      {/* 主要内容区域 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 左侧：请求配置 */}
        <div className="space-y-6">
          {/* 服务信息卡片 */}
          {selectedService && (
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl shadow-sm border border-blue-200 p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Info className="w-4 h-4" />
                服务信息
              </h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-600">名称：</span>
                  <span className="font-medium">{selectedService.name}</span>
                </div>
                <div>
                  <span className="text-gray-600">类型：</span>
                  <span className="font-medium">{selectedService.provider}</span>
                </div>
                <div>
                  <span className="text-gray-600">积分：</span>
                  <span className="font-medium text-blue-600">{selectedService.points_cost}</span>
                </div>
                <div>
                  <span className="text-gray-600">超时：</span>
                  <span className="font-medium">{selectedService.timeout_seconds}s</span>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-600">UUID：</span>
                  <code className="text-xs bg-white px-2 py-0.5 rounded border border-gray-200">
                    {selectedService.id}
                  </code>
                </div>
              </div>
            </div>
          )}

          {/* 测试参数编辑器 */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-900">测试参数</h3>
              {selectedService && (
                <button
                  onClick={() => loadTemplate(selectedService.provider)}
                  className="text-xs px-3 py-1.5 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 transition"
                >
                  加载模板
                </button>
              )}
            </div>
            <textarea
              value={testParams}
              onChange={(e) => setTestParams(e.target.value)}
              rows={12}
              className={`w-full px-4 py-3 rounded-lg border ${parseError ? 'border-red-300 bg-red-50' : 'border-gray-300'} focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all font-mono text-sm resize-none`}
              placeholder='{"parameters": {"input": "你好"}}'
            />
            {parseError && (
              <p className="mt-2 text-xs text-red-600 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {parseError}
              </p>
            )}
          </div>

          {/* 请求预览 */}
          {requestInfo && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Info className="w-4 h-4" />
                请求预览
              </h3>
              <div className="space-y-3 text-xs font-mono">
                <div>
                  <span className="text-gray-500 font-sans block mb-1">URL:</span>
                  <div className="text-blue-600 break-all bg-blue-50 p-2 rounded border border-blue-200">
                    {requestInfo.method} {requestInfo.url}
                  </div>
                </div>
                <div>
                  <span className="text-gray-500 font-sans block mb-1">Headers:</span>
                  <pre className="bg-gray-50 p-2 rounded border border-gray-200 overflow-x-auto whitespace-pre-wrap break-all text-[11px]">
                    {formatHeaders(requestInfo.headers)}
                  </pre>
                </div>
                <div>
                  <span className="text-gray-500 font-sans block mb-1">Body:</span>
                  <pre className="bg-gray-50 p-2 rounded border border-gray-200 overflow-x-auto max-h-48 overflow-y-auto text-[11px]">
                    {requestInfo.body}
                  </pre>
                </div>
              </div>
            </div>
          )}

          {/* 发送按钮 */}
          <button
            onClick={handleTest}
            disabled={testing || !selectedService || !selectedTestApiKey || !!parseError}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-lg"
            title={
              testing ? '测试中...' :
              !selectedService ? '请选择服务' :
              !selectedTestApiKey ? '请输入 API Key' :
              parseError ? `JSON 格式错误: ${parseError}` :
              '点击发送测试请求（Ctrl+Enter）'
            }
          >
            {testing ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                测试中...
              </>
            ) : (
              <>
                <Play className="w-5 h-5" />
                发送测试请求
                <span className="text-xs opacity-75 ml-2">(Ctrl+Enter)</span>
              </>
            )}
          </button>
        </div>

        {/* 右侧：响应结果 */}
        <div className="space-y-6">
          {/* 响应头信息 */}
          {testResult && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  {testSuccess ? (
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-red-500" />
                  )}
                  测试结果
                </h3>
                <div className="flex items-center gap-2">
                  {responseTime > 0 && (
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      testSuccess ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {responseTime}ms
                    </span>
                  )}
                  <button
                    onClick={() => copyToClipboard(testResult)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition"
                    title="复制结果"
                  >
                    <Copy className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
              </div>
              <pre className="bg-gray-900 text-green-400 p-4 rounded-lg text-[11px] font-mono max-h-[600px] overflow-auto">
                {testResult}
              </pre>
            </div>
          )}

          {!testResult && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Play className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-600 font-medium mb-2">等待测试</p>
              <p className="text-sm text-gray-400">配置好参数后点击发送按钮开始测试</p>
            </div>
          )}

          {/* 响应时间图表 */}
          {chartData.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                响应时间趋势（最近 10 次）
              </h3>
              <div className="space-y-2">
                {chartData.map((item, index) => {
                  const maxTime = Math.max(...chartData.map(d => d.responseTime))
                  const width = (item.responseTime / maxTime) * 100
                  return (
                    <div key={item.id} className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 w-4">{chartData.length - index}</span>
                      <div className="flex-1 h-6 bg-gray-100 rounded-lg overflow-hidden relative">
                        <div
                          className={`h-full ${item.success ? 'bg-gradient-to-r from-green-400 to-green-500' : 'bg-gradient-to-r from-red-400 to-red-500'} transition-all`}
                          style={{ width: `${width}%` }}
                        />
                        <span className="absolute inset-0 flex items-center px-2 text-xs font-medium text-gray-700">
                          {item.responseTime}ms
                        </span>
                      </div>
                      <span className="text-xs text-gray-400 w-32 truncate" title={item.serviceName}>
                        {item.serviceName}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 历史记录侧边栏 */}
      {showHistory && (
        <div className="fixed inset-0 z-50 flex items-center justify-end bg-black/50 backdrop-blur-sm" onClick={() => setShowHistory(false)}>
          <div className="bg-white h-full w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Clock className="w-6 h-6" />
                <div>
                  <h3 className="text-lg font-bold">测试历史</h3>
                  <p className="text-sm text-purple-100">共 {history.length} 条记录</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={clearHistory}
                  className="p-2 hover:bg-white/20 rounded-lg transition"
                  title="清空历史"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setShowHistory(false)}
                  className="p-2 hover:bg-white/20 rounded-lg transition"
                >
                  <span className="text-xl">×</span>
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-3">
              {history.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>暂无测试历史</p>
                </div>
              ) : (
                history.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => loadFromHistory(item)}
                    className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-purple-300 hover:shadow-md transition cursor-pointer"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900">{item.serviceName}</h4>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                          item.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {item.responseTime}ms
                        </span>
                        {item.statusCode && (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            item.statusCode >= 200 && item.statusCode < 300
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {item.statusCode}
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mb-2">
                      {new Date(item.timestamp).toLocaleString('zh-CN')}
                    </p>
                    <pre className="text-xs bg-white p-2 rounded border border-gray-200 overflow-x-auto max-h-24 overflow-y-auto">
                      {item.params}
                    </pre>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
